import React, { useState, useEffect, useCallback } from 'react';
import {
  Key, Check, X, Loader2, Eye, EyeOff,
  Save, RefreshCw, MessageCircle, Mail, Link2, Copy,
  CheckCircle, XCircle, AlertCircle, Zap, Info,
  ChevronDown, ChevronUp, Image, CreditCard, BookOpen, MessageSquareText
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';

// ── Service icon ──────────────────────────────────────────────────────────
function ServiceIcon({ icon, connected }) {
  const base = `w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0`;
  const on   = connected ? '' : 'opacity-60';
  if (icon === 'whatsapp')   return <div className={`${base} bg-emerald-500 ${on}`}><MessageCircle className="w-5 h-5 text-white" /></div>;
  if (icon === 'cloudinary') return <div className={`${base} bg-violet-500 ${on}`}><Image className="w-5 h-5 text-white" /></div>;
  if (icon === 'paystack')   return <div className={`${base} bg-teal-500 ${on}`}><CreditCard className="w-5 h-5 text-white" /></div>;
  if (icon === 'sms')        return <div className={`${base} bg-blue-500 ${on}`}><MessageSquareText className="w-5 h-5 text-white" /></div>;
  return                            <div className={`${base} bg-blue-500 ${on}`}><Mail className="w-5 h-5 text-white" /></div>;
}

// ── Connection badge ──────────────────────────────────────────────────────
function ConnBadge({ connected }) {
  return connected ? (
    <span className="flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-100 px-2.5 py-1 rounded-full">
      <CheckCircle className="w-3.5 h-3.5" /> Connected
    </span>
  ) : (
    <span className="flex items-center gap-1 text-xs font-bold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
      <XCircle className="w-3.5 h-3.5" /> Not configured
    </span>
  );
}

// ── Secret field ──────────────────────────────────────────────────────────
function SecretInput({ field, value, onChange }) {
  const [show, setShow] = useState(false);

  if (!onChange && field.is_set) {
    return (
      <div className="flex items-center gap-2">
        <code className="text-sm font-mono text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 flex-1">
          {field.secret ? (field.masked || '—') : (field.masked || '—')}
        </code>
        <span className="text-xs text-emerald-600 font-semibold bg-emerald-50 px-2 py-1 rounded-md">Set</span>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="relative">
        <input
          type={show || !field.secret ? 'text' : 'password'}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={field.is_set ? `Keep current (${field.masked || 'set'})` : field.placeholder}
          className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-400 transition-colors font-mono pr-10"
        />
        {field.secret && (
          <button type="button" onClick={() => setShow(s => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
      </div>
      {field.help && <p className="text-xs text-slate-400">{field.help}</p>}
    </div>
  );
}

// ── Setup Guide Accordion ─────────────────────────────────────────────────
function SetupGuide({ steps, title }) {
  const [open, setOpen] = useState(false);
  if (!steps?.length) return null;
  return (
    <div className="border border-slate-100 rounded-2xl overflow-hidden">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
        <span className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-slate-400" />
          {title || 'Setup Guide'}
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
          {steps.map(s => (
            <div key={s.step} className="flex gap-3">
              <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                {s.step}
              </span>
              <div>
                <p className="text-sm font-semibold text-slate-700">{s.title}</p>
                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{s.detail}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Webhook Instructions Accordion ───────────────────────────────────────
function WebhookInstructions({ steps }) {
  const [open, setOpen] = useState(false);
  if (!steps?.length) return null;
  return (
    <div className="border border-amber-100 bg-amber-50/40 rounded-2xl overflow-hidden mt-2">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-amber-700 hover:bg-amber-50 transition-colors">
        <span className="flex items-center gap-2">
          <Info className="w-4 h-4" />
          How to activate real-time event tracking
        </span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-amber-100 pt-3">
          {steps.map(s => (
            <div key={s.step} className="flex gap-3">
              <span className="w-5 h-5 rounded-full bg-amber-200 text-amber-800 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                {s.step}
              </span>
              <div>
                <p className="text-sm font-semibold text-amber-800">{s.title}</p>
                <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">{s.detail}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Copy button with state ────────────────────────────────────────────────
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={copy}
      className="flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-700 bg-white border border-slate-200 px-2.5 py-1.5 rounded-lg transition-all flex-shrink-0">
      {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
      Copy
    </button>
  );
}

// ── Service Card ──────────────────────────────────────────────────────────
function ServiceCard({ svcId, svc, onSaved }) {
  const [editing, setEditing]       = useState(false);
  const [values, setValues]         = useState({});
  const [saving, setSaving]         = useState(false);
  const [testing, setTesting]       = useState(false);
  const [testResult, setTestResult] = useState(null);

  const handleSave = async () => {
    const payload = {};
    svc.fields.forEach(f => {
      if (values[f.key] !== undefined && values[f.key] !== '') {
        payload[f.key] = values[f.key];
      }
    });
    if (Object.keys(payload).length === 0) {
      toast.error('No changes to save');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/master/integrations/${svcId}`, payload);
      toast.success('Credentials saved');
      setEditing(false);
      setValues({});
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post(`/master/integrations/${svcId}/test`);
      setTestResult({ ok: r.success, msg: r.message });
      if (r.success) toast.success(r.message);
      else toast.error(r.message);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.detail || 'Connection test failed';
      setTestResult({ ok: false, msg });
      toast.error(msg);
    } finally { setTesting(false); }
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden" data-testid={`integration-card-${svcId}`}>
      {/* Card header */}
      <div className="flex items-center justify-between p-6 pb-4">
        <div className="flex items-center gap-3">
          <ServiceIcon icon={svc.icon} connected={svc.is_connected} />
          <div>
            <h3 className="font-bold text-slate-900 text-base">{svc.label}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{svc.description}</p>
          </div>
        </div>
        <ConnBadge connected={svc.is_connected} />
      </div>

      <div className="px-6 pb-6 space-y-4">
        {/* Credential fields */}
        <div className="space-y-3.5">
          {svc.fields.map(field => (
            <div key={field.key}>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">
                {field.label}
                {field.optional && <span className="ml-1 text-slate-400 normal-case font-normal">(optional)</span>}
                {field.is_set && !editing && <span className="ml-1 text-emerald-500 font-bold">✓</span>}
              </label>
              <SecretInput
                field={field}
                value={values[field.key] ?? ''}
                onChange={editing ? v => setValues(prev => ({ ...prev, [field.key]: v })) : null}
              />
            </div>
          ))}
        </div>

        {/* Readonly fields */}
        {svc.readonly_fields?.length > 0 && (
          <div className="space-y-2.5">
            {svc.readonly_fields.map(rf => (
              <div key={rf.key}>
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1 block">{rf.label}</label>
                <code className="text-sm font-mono text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 block">
                  {rf.value || '—'}
                </code>
              </div>
            ))}
          </div>
        )}

        {/* Webhook info */}
        {svc.webhook && (
          <div className="bg-slate-50 rounded-2xl p-4 space-y-2">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
              <Link2 className="w-3.5 h-3.5" /> Webhook URL
            </p>
            <div className="flex items-center justify-between gap-2">
              <code className="text-xs font-mono text-slate-600 truncate">{svc.webhook.url}</code>
              <CopyButton text={svc.webhook.url} />
            </div>
            {svc.webhook.verify_token && (
              <p className="text-xs text-slate-500">
                Verify token: <code className="text-slate-600 bg-white border border-slate-200 px-1.5 py-0.5 rounded">{svc.webhook.verify_token}</code>
              </p>
            )}
          </div>
        )}

        {/* Webhook setup instructions (e.g. Resend) */}
        {svc.webhook_setup_steps?.length > 0 && (
          <WebhookInstructions steps={svc.webhook_setup_steps} />
        )}

        {/* Setup guide (e.g. WhatsApp Meta steps) */}
        {svc.setup_steps?.length > 0 && (
          <SetupGuide steps={svc.setup_steps} title="Meta Developer App Setup Guide" />
        )}

        {/* Test result */}
        {testResult && (
          <div className={`rounded-xl p-3 flex items-center gap-2.5 text-sm ${testResult.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
            {testResult.ok ? <CheckCircle className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
            {testResult.msg}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-2 pt-1">
          {editing ? (
            <>
              <button onClick={() => { setEditing(false); setValues({}); }}
                className="flex-1 py-2.5 rounded-xl font-semibold text-sm border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                className="flex-1 py-2.5 rounded-xl font-bold text-sm text-white bg-[#2563eb] hover:bg-blue-700 transition-all flex items-center justify-center gap-1.5 disabled:opacity-60"
                data-testid={`save-${svcId}-btn`}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Credentials
              </button>
            </>
          ) : (
            <>
              <button onClick={handleTest} disabled={testing || !svc.is_connected}
                title={!svc.is_connected ? 'Save credentials first' : 'Test connection'}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl font-semibold text-sm border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 transition-all"
                data-testid={`test-${svcId}-btn`}>
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                Test
              </button>
              <button onClick={() => { setEditing(true); setTestResult(null); }}
                className="flex-1 py-2.5 rounded-xl font-bold text-sm text-white bg-slate-800 hover:bg-slate-700 transition-all flex items-center justify-center gap-1.5"
                data-testid={`edit-${svcId}-btn`}>
                <Key className="w-4 h-4" />
                {svc.is_connected ? 'Update Credentials' : 'Add Credentials'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────
export default function IntegrationsPage() {
  const [services, setServices] = useState({});
  const [loading, setLoading]   = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/master/integrations');
      setServices(r.data || {});
    } catch {
      toast.error('Failed to load integrations');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const connectedCount = Object.values(services).filter(s => s.is_connected).length;
  const totalCount     = Object.keys(services).length;

  return (
    <div className="px-6 md:px-8 py-6 md:py-8" data-testid="integrations-page">
      {/* Header */}
      <div className="relative bg-[#0f172a] rounded-3xl overflow-hidden mb-8" style={{ minHeight: 120 }}>
        <div className="absolute inset-0 opacity-5"
          style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
        <div className="relative flex items-start justify-between gap-4 p-7">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center">
              <Key className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold text-white tracking-tight">Integrations & Credentials</h1>
              <p className="text-slate-400 text-sm mt-0.5">Connect third-party services to power EDUVA features</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-300 bg-white/10 px-3 py-1.5 rounded-full">
              {connectedCount}/{totalCount} Connected
            </span>
            <button onClick={load} className="p-2 rounded-xl bg-white/10 hover:bg-white/15 transition-all" title="Refresh"
              data-testid="refresh-integrations-btn">
              <RefreshCw className={`w-4 h-4 text-white ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Info note */}
      <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 mb-7 flex items-start gap-3">
        <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-blue-700">
          Credentials are stored securely and take effect <strong>immediately</strong> — no server restart needed.
          Secrets are never shown in full after saving.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-slate-300" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {Object.entries(services).map(([svcId, svc]) => (
            <ServiceCard key={svcId} svcId={svcId} svc={svc} onSaved={load} />
          ))}

          {/* Stripe coming soon */}
          <div className="bg-white rounded-3xl border border-dashed border-slate-200 p-6 flex items-center gap-4 opacity-50">
            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-xl">💳</div>
            <div>
              <h3 className="font-bold text-slate-700">Stripe</h3>
              <p className="text-xs text-slate-400">International payment processing</p>
            </div>
            <span className="ml-auto text-xs font-bold text-slate-400 bg-slate-100 px-2.5 py-1 rounded-full">Coming soon</span>
          </div>
        </div>
      )}
    </div>
  );
}
